// Playback (sim-time) awareness — the one switch every page's clock goes
// through.
//
// A boat replaying a recording runs on a virtual clock and its data carries
// the RECORDING's timestamps. Judged against the browser's wall clock, that
// data reads as broken: every freshness dot goes cold, and a "last 6 h" track
// window brackets nothing. The fix is not to fork any component — it is that
// each page's single `nowMs` tick asks this module for the time first, and
// gets the boat's virtual now while a playback is active.
//
// Mixed comparisons stay safe by construction: during a replay of a PAST
// recording, virtual now < wall now, so a wall-stamped fact (the presence
// chip's `last_contacted_at`) compared against virtual now yields a NEGATIVE
// age — and every freshness helper here already clamps negative ages to
// "fresh"/"Just now", which is true (the boat is genuinely connected).

import { simVirtualNowMs, type SimTimeStatus } from '@neomaris/protocol';

export type { SimTimeStatus };

/**
 * The clock a boat-scoped page should run on: the boat's virtual now while a
 * playback is active, the wall clock otherwise. The pair in `sim` is a linear
 * correspondence, so a heartbeat-stale pair still extrapolates exactly — no
 * polling needed to keep a ticking recording clock honest.
 */
export function playbackNowMs(
  sim: SimTimeStatus | null | undefined,
  wallNowMs: number = Date.now()
): number {
  if (!sim) return wallNowMs;
  return simVirtualNowMs(sim, wallNowMs);
}

/**
 * The within-episode recording clock for a LOOPED replay: the absolute virtual
 * clock folded back into `[anchor, anchor + span)`. Each loop's state resets
 * while the virtual timeline keeps advancing, so the honest per-episode time
 * is the offset into the recording, not the raw virtual instant. Falls back
 * to the absolute clock when the boat did not report a span. Double-mod
 * guards an instant momentarily behind the anchor (clock skew).
 */
export function episodeClockMs(sim: SimTimeStatus, wallNowMs: number): number {
  const virtual = simVirtualNowMs(sim, wallNowMs);
  if (!sim.spanMs || sim.spanMs <= 0) return virtual;
  const anchor = Date.parse(sim.anchorIso);
  const offset = (((virtual - anchor) % sim.spanMs) + sim.spanMs) % sim.spanMs;
  return anchor + offset;
}

/** Recording clock as "2026-08-21 09:42:13 UTC" — visibly a date, not a time-ago. */
export function formatRecordingClock(virtualNowMs: number): string {
  return new Date(virtualNowMs)
    .toISOString()
    .replace('T', ' ')
    .replace(/\.\d+Z$/, ' UTC');
}
